import React, { FC, ReactNode, useContext } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { ThemeContext } from "styled-components";
import { TenantContext } from "v3/context/tenant";
import { LocalizationContext } from "v3/context/localization";
import { SimpleHeader } from "@pepdirect/v3/ui/SimpleHeader";
import { CaretIconSvg } from "@pepdirect/v3/ui/icons";
import { LoadingPage } from "v3/components/LoadingPage";
import {
  BrandConfigCustomizationsImage,
  useCurrentUserIdQuery,
} from "v3/services/graphql/generated";
import { PageRoutes } from "v3/constants/pageRoutes";
import {
  StyledAccountContent,
  StyledImage,
  StyledLinkWrapper,
  StyledMain,
} from "./index.styled";

interface PortalPublicPageWrapperProps {
  children: ReactNode;
  hideBackButton?: boolean;
  title: string;
}

/**
 * Used on pages in portal where a user is not logged in
 */
export const PortalPublicPageWrapper: FC<PortalPublicPageWrapperProps> = ({
  children,
  hideBackButton = false,
  title,
}) => {
  const theme = useContext(ThemeContext);
  const { tenant } = useContext(TenantContext);
  const { localization } = useContext(LocalizationContext);
  const { accountAltText = "Account", shopLogoAltText = "shop logo" } =
    localization?.portal?.alts || {};
  const fallbackUrl = tenant?.fallbackUrl || "";
  const logo =
    tenant?.brandConfig?.customizations?.images?.find(
      (img: BrandConfigCustomizationsImage) => img.name === "logoSmallUrl"
    ) || null;
  const accountIcon = tenant?.brandConfig?.customizations?.images?.find(
    (img: BrandConfigCustomizationsImage) => img.name === "accountIconUrl"
  );
  const router = useRouter();
  const { data: currentUserId, loading: currentUserIdLoading } =
    useCurrentUserIdQuery();

  if (currentUserIdLoading) return <LoadingPage />;

  if (currentUserId?.currentUser) {
    router.push(PageRoutes.account);
    return null;
  }

  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>

      {!accountIcon && (
        <SimpleHeader
          fallbackUrl={fallbackUrl}
          logo={{ src: logo?.src || "", alt: logo?.alt || shopLogoAltText }}
          type="portal"
        />
      )}

      {accountIcon && (
        <div>
          <StyledAccountContent>
            {!hideBackButton && (
              <StyledLinkWrapper onClick={router.back}>
                <CaretIconSvg
                  pointDirection="left"
                  color={theme.color.primary}
                  width="14px"
                  height="12px"
                />
              </StyledLinkWrapper>
            )}
            <StyledImage
              src={accountIcon.src}
              alt={accountIcon.alt || accountAltText}
            />
          </StyledAccountContent>
        </div>
      )}
      <StyledMain>{children}</StyledMain>
    </>
  );
};
